import { InfoCircleOutlined } from '@ant-design/icons';

import { Divider, VFlex } from '@/components/base';
import KeyboardNavigator from '@/components/KeyboardNavigator/KeyboardNavigator';
import type {
  KeyboardNavigatorKeymap,
  SelectedNavigatorItem,
} from '@/components/KeyboardNavigator/types';
import List from '@/components/List/List';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';

import type {
  ExtendedFilterQueryFormatT,
  SelectableItemT,
} from './searchQueryBuilderTypes';
import SearchQueryBuilderStyles from './styles';

const {
  SelectListWrapperStyle,
  SectionTitleText,
  MatchedValueText,
  FilterValueText,
} = SearchQueryBuilderStyles;

type Props = {
  searchForList: SelectableItemT[];
  selectedItem: SelectedNavigatorItem | null;
  setSelectedItem: (item: SelectedNavigatorItem | null) => void;
  keymap: KeyboardNavigatorKeymap | null;
  debouncedInput: string;
  data: ExtendedFilterQueryFormatT[] | null;
  isLoading: boolean;
};

const DefaultSearchTargetView = (props: Props) => {
  const {
    searchForList,
    selectedItem,
    setSelectedItem,
    keymap,
    debouncedInput,
    data,
    isLoading,
  } = props;

  if (keymap === null || keymap.length === 0) return null;

  const textSearchMenuItems = [
    {
      label: 'Text similar to',
      action: () => keymap[0][0].action(),
      tooltip:
        'Syncly AI to run an in-depth search by finding semantically similar feedback. Ask any questions in natural language and describe what you are looking for in more details for the best results. Please allow Syncly few minutes to complete this search.',
    },
    {
      label: 'Text include',
      action: () => keymap[0][1].action(),
      tooltip:
        'Exact keyword matching is the best method when you are searching for specific issues that are described in certain ways. You can try different keyword combinations for the best results.',
    },
  ];

  return (
    <SelectListWrapperStyle>
      <KeyboardNavigator.List f={3}>
        <SectionTitleText>Text search</SectionTitleText>
        <VFlex g={2}>
          {textSearchMenuItems.map((item, index) => (
            <KeyboardNavigator.Item
              key={item.label}
              selected={selectedItem?.col === 0 && selectedItem.index === index}
              onMouseEnter={() => {
                setSelectedItem({
                  col: 0,
                  index: index,
                });
              }}
              onClick={() => keymap[0][index].action()}
            >
              <Typo>
                <MatchedValueText
                  text={debouncedInput}
                  matchedValue={debouncedInput}
                />
                <Typo type="label3" weight="r" className="text-gray-600">
                  &nbsp;&nbsp;&nbsp;- {item.label} "{debouncedInput}"
                </Typo>
                &nbsp;&nbsp;
                <TooltipV2 tooltipNode={item.tooltip}>
                  <InfoCircleOutlined />
                </TooltipV2>
              </Typo>
            </KeyboardNavigator.Item>
          ))}
        </VFlex>
        <SectionTitleText>Best matches</SectionTitleText>
        <List
          className="gap-0.5 flex-1 overflow-unset"
          data={data || []}
          isLoading={isLoading}
          render={(value, index) => {
            const offsetIndex = index + textSearchMenuItems.length;
            return (
              <KeyboardNavigator.Item
                key={`${value.columnKey}.${value.filterValues[0].filterKey}`}
                selected={
                  selectedItem?.col === 0 && selectedItem.index === offsetIndex
                }
                onMouseEnter={() => {
                  setSelectedItem({
                    col: 0,
                    index: offsetIndex,
                  });
                }}
                onClick={() => keymap[0]?.[offsetIndex]?.action()}
              >
                <FilterValueText
                  matchedValue={debouncedInput}
                  filterQueryFormat={value}
                />
              </KeyboardNavigator.Item>
            );
          }}
        />
      </KeyboardNavigator.List>
      <Divider direction="v" />
      <KeyboardNavigator.List f={2}>
        <SectionTitleText>Search for</SectionTitleText>
        <VFlex>
          {searchForList.map((item, index) => (
            <KeyboardNavigator.Item
              key={item.value}
              selected={selectedItem?.col === 1 && selectedItem.index === index}
              onMouseEnter={() => {
                setSelectedItem({
                  col: 1,
                  index,
                });
              }}
              onClick={() => keymap[1][index].action()}
            >
              <Typo type="body3" className="text-gray-600">
                {item.label}&nbsp;&nbsp;
                <MatchedValueText
                  text={debouncedInput}
                  matchedValue={debouncedInput}
                />
                ...
              </Typo>
            </KeyboardNavigator.Item>
          ))}
        </VFlex>
      </KeyboardNavigator.List>
    </SelectListWrapperStyle>
  );
};

export default DefaultSearchTargetView;
